import { gql } from '@apollo/client';

// User and Profile queries
export const GET_CURRENT_USER = gql`
  query GetCurrentUser($userId: uuid!) {
    user_by_pk(id: $userId) {
      id
      data
      person {
        id
        data
        families {
          family {
            id
            name
            slug
            data
          }
        }
      }
    }
  }
`;

export const GET_PROFILE = gql`
  query GetProfile($personId: String!) {
    people_by_pk(id: $personId) {
      id
      uid
      familyIds
      data
      createdAt
      modifiedAt
    }
  }
`;

// Family queries
export const GET_FAMILY = gql`
  query GetFamily {
    family {
      id
      name
      slug
      data
      members {
        person_id
        role
        color
        email_forward
        person {
          id
          first_name
          last_name
          email
          phone
          gender
          birth_date
          data
          profile_photo_id
          profile_photo {
            id
            uri
            bucket
            key
            thumbnail_key
            mini_preview
          }
          user {
            id
          }
        }
      }
    }
  }
`;

export const GET_FAMILIES = gql`
  query GetFamilies($familyIds: jsonb!) {
    families(where: { id: { _in: $familyIds } }) {
      id
      memberIds
      data
      createdAt
      modifiedAt
    }
  }
`;

export const GET_FAMILY_MEMBERS = gql`
  query GetFamilyMembers($familyIds: jsonb!) {
    people(where: { familyIds: { _contains: $familyIds } }) {
      id
      uid
      familyIds
      data
      createdAt
      modifiedAt
    }
  }
`;

// Stats queries for home screen - uses family_data table
// Note: family_id filtering is handled by Hasura permissions
export const GET_HOME_STATS = gql`
  query GetHomeStats {
    # All family data for counting by type
    family_data(
      where: {
        status: { _neq: "D" }
      }
    ) {
      id
      data {
        id
        type
        data
      }
    }
  }
`;

// Person data stats for health counts
export const GET_PERSON_DATA_STATS = gql`
  query GetPersonDataStats($person_ids: [uuid!]!) {
    person_data(
      where: {
        person_id: { _in: $person_ids }
        status: { _neq: "D" }
      }
    ) {
      id
      person_id
      data {
        id
        type
        data
      }
    }
  }
`;

// Real-time subscriptions for home screen stats
// Note: family_id filtering is handled by Hasura permissions
export const SUBSCRIBE_HOME_STATS = gql`
  subscription SubscribeHomeStats {
    family_data(
      where: {
        status: { _neq: "D" }
      }
    ) {
      id
      data {
        type
      }
    }
  }
`;

export const SUBSCRIBE_PERSON_DATA_STATS = gql`
  subscription SubscribePersonDataStats($person_ids: [uuid!]!) {
    person_data(
      where: {
        person_id: { _in: $person_ids }
        status: { _neq: "D" }
      }
      order_by: { modified_at: desc }
    ) {
      id
      person_id
      data {
        id
        type
        data
      }
      status
      created_at
      modified_at
    }
  }
`;

// ===========================================
// PERSON DATA QUERIES AND MUTATIONS
// ===========================================
// Data structure: { person_id, data: { id, type, data: {...} } }
// Similar to family_data, allows multiple people to share the same data record

// Query to fetch all person data
export const GET_PERSON_DATA = gql`
  query GetPersonData($person_id: uuid!) {
    person_data(
      where: {
        person_id: { _eq: $person_id }
        status: { _neq: "D" }
      }
      order_by: { modified_at: desc }
    ) {
      id
      person_id
      data {
        id
        type
        data
      }
      status
      created_at
      modified_at
    }
  }
`;

// Subscription for real-time person data updates
export const SUBSCRIBE_PERSON_DATA = gql`
  subscription SubscribePersonData($person_id: uuid!) {
    person_data(
      where: {
        person_id: { _eq: $person_id }
        status: { _neq: "D" }
      }
      order_by: { modified_at: desc }
    ) {
      id
      person_id
      data {
        id
        type
        data
      }
      status
      created_at
      modified_at
    }
  }
`;

// Query to fetch person data for multiple family members
// Client-side filtering by type is done in useFamilyMembersData hook
export const GET_FAMILY_MEMBERS_DATA = gql`
  query GetFamilyMembersData($person_ids: [uuid!]!) {
    person_data(
      where: {
        person_id: { _in: $person_ids }
        status: { _neq: "D" }
      }
      order_by: { modified_at: desc }
    ) {
      id
      person_id
      data {
        id
        type
        data
      }
      status
      created_at
      modified_at
    }
  }
`;

// Insert person data
// Creates a person_data record with nested data object
export const INSERT_PERSON_DATA = gql`
  mutation InsertPersonData($object: person_data_insert_input!) {
    insert_person_data_one(object: $object) {
      id
      person_id
      data {
        id
        type
        data
      }
      status
      created_at
      modified_at
    }
  }
`;

// Update person data
// Updates the data table directly (similar to UPDATE_FAMILY_DATA)
export const UPDATE_PERSON_DATA = gql`
  mutation UpdatePersonData($id: uuid!, $type: String!, $data: jsonb!) {
    update_data_by_pk(
      pk_columns: { id: $id }
      _set: { type: $type, data: $data }
    ) {
      id
      type
      data
    }
  }
`;

// Soft delete person data
export const DELETE_PERSON_DATA = gql`
  mutation DeletePersonData($id: uuid!) {
    update_person_data_by_pk(
      pk_columns: { id: $id }
      _set: { status: "D" }
    ) {
      id
      status
    }
  }
`;

// Insert data record with nested person_data links (one transaction)
export const INSERT_SHARED_PERSON_DATA = gql`
  mutation InsertSharedPersonData($type: String!, $data: jsonb!, $people: [person_data_insert_input!]!) {
    insert_data_one(object: {
      type: $type,
      data: $data,
      people: { data: $people }
    }) {
      id
      type
      data
      people {
        id
        person_id
        data_id
        status
        data {
          id
          type
          data
        }
      }
    }
  }
`;

// Add person links to existing shared data record
export const INSERT_PERSON_DATA_LINKS = gql`
  mutation InsertPersonDataLinks($objects: [person_data_insert_input!]!) {
    insert_person_data(objects: $objects) {
      returning {
        id
        person_id
        data_id
        status
      }
    }
  }
`;

// Remove person links from shared data (soft delete by person_id and data_id)
export const DELETE_PERSON_DATA_LINKS = gql`
  mutation DeletePersonDataLinks($data_id: uuid!, $person_ids: [uuid!]!) {
    update_person_data(
      where: {
        data_id: { _eq: $data_id }
        person_id: { _in: $person_ids }
      }
      _set: { status: "D" }
    ) {
      affected_rows
    }
  }
`;

// Mutations
export const UPDATE_PERSON = gql`
  mutation UpdatePerson(
    $id: uuid!
    $firstName: String!
    $lastName: String!
    $email: String
    $phone: String
    $gender: String
    $birthDate: date
    $profilePhotoId: uuid
  ) {
    update_person_by_pk(
      pk_columns: { id: $id }
      _set: {
        first_name: $firstName
        last_name: $lastName
        email: $email
        phone: $phone
        gender: $gender
        birth_date: $birthDate
        profile_photo_id: $profilePhotoId
      }
    ) {
      id
      first_name
      last_name
      email
      phone
      gender
      birth_date
      profile_photo_id
    }
  }
`;

export const UPDATE_FAMILY_MEMBER = gql`
  mutation UpdateFamilyMember(
    $familyId: uuid!
    $personId: uuid!
    $role: String!
    $color: String
    $email_forward: Boolean
  ) {
    update_family_member_by_pk(
      pk_columns: { family_id: $familyId, person_id: $personId }
      _set: { role: $role, color: $color, email_forward: $email_forward }
    ) {
      family_id
      person_id
      role
      color
      email_forward
    }
  }
`;

export const CREATE_PERSON = gql`
  mutation CreatePerson($id: String!, $familyIds: jsonb!, $data: jsonb!) {
    insert_people_one(
      object: {
        id: $id
        familyIds: $familyIds
        data: $data
        createdAt: "now()"
        modifiedAt: "now()"
      }
    ) {
      id
      familyIds
      data
    }
  }
`;

// Family setup mutations
export const CREATE_FAMILY = gql`
  mutation CreateFamily(
    $name: String!
    $slug: String!
    $data: jsonb
    $createdBy: uuid!
  ) {
    insert_family_one(
      object: {
        name: $name
        slug: $slug
        status: "A"
        data: $data
        created_by: $createdBy
      }
    ) {
      id
      name
      slug
    }
  }
`;

export const UPDATE_FAMILY = gql`
  mutation UpdateFamily($id: uuid!, $data: jsonb!) {
    update_family_by_pk(pk_columns: { id: $id }, _set: { data: $data }) {
      id
      name
      slug
      data
    }
  }
`;

export const ADD_FAMILY_MEMBER = gql`
  mutation AddFamilyMember(
    $familyId: uuid!
    $personId: uuid!
    $role: String!
  ) {
    insert_family_member_one(
      object: {
        family_id: $familyId
        person_id: $personId
        role: $role
      }
    ) {
      family_id
      person_id
      role
      person {
        id
        first_name
        last_name
        email
      }
    }
  }
`;

export const INSERT_FAMILY_MEMBER = gql`
  mutation InsertFamilyMember(
    $familyId: uuid!
    $role: String!
    $color: String
    $firstName: String!
    $lastName: String!
    $email: String
    $phone: String
    $gender: String
    $birthDate: date
  ) {
    insert_person_one(
      object: {
        first_name: $firstName
        last_name: $lastName
        email: $email
        phone: $phone
        gender: $gender
        birth_date: $birthDate
        families: {
          data: {
            family_id: $familyId
            role: $role
            color: $color
          }
        }
      }
    ) {
      id
      first_name
      last_name
      email
      birth_date
      families {
        family_id
        person_id
        role
        color
      }
    }
  }
`;

export const DELETE_FAMILY_MEMBER = gql`
  mutation DeleteFamilyMember($familyId: uuid!, $personId: uuid!) {
    delete_family_member_by_pk(family_id: $familyId, person_id: $personId) {
      family_id
      person_id
    }
  }
`;


// ============================================
// EVENT QUERIES AND MUTATIONS
// ============================================

// Shared fragment for event fields
export const EVENT_FIELDS = gql`
  fragment EventFields on event {
    id
    title
    description
    location
    start_time
    end_time
    is_all_day
    calendar
    families {
      family_id
    }
    rrule
    repeat
    alert
    tags
    notes
    url
    created_by
    created_at
    modified_at
    deleted_at
    data
    participants {
      id
      event_id
      person_id
      status
      role
      notes
      created_at
      modified_at
    }
  }
`;

// Get events by date range (excludes Custody events)
// Uses _or to include recurring events whose base date is before the window
// Note: family_id filtering is handled by Hasura permissions
export const GET_EVENTS = gql`
  ${EVENT_FIELDS}
  query GetEvents($start_date: timestamptz!, $end_date: timestamptz!) {
    event(
      where: {
        deleted_at: { _is_null: true }
        calendar: { _neq: "Custody" }
        _or: [
          { start_time: { _gte: $start_date }, end_time: { _lte: $end_date } }
          { rrule: { _is_null: false }, start_time: { _lte: $end_date } }
        ]
      }
      order_by: { start_time: asc }
    ) {
      ...EventFields
    }
  }
`;

// Get a single event by ID
export const GET_EVENT = gql`
  ${EVENT_FIELDS}
  query GetEvent($id: uuid!) {
    event_by_pk(id: $id) {
      ...EventFields
    }
  }
`;

// Search events by text (excludes Custody events)
// Note: family_id filtering is handled by Hasura permissions
export const SEARCH_EVENTS = gql`
  ${EVENT_FIELDS}
  query SearchEvents($search: String!) {
    event(
      where: {
        deleted_at: { _is_null: true }
        calendar: { _neq: "Custody" }
        _or: [
          { title: { _ilike: $search } }
          { description: { _ilike: $search } }
          { notes: { _ilike: $search } }
        ]
      }
      order_by: { start_time: asc }
    ) {
      ...EventFields
    }
  }
`;

// Create a new event with optional nested participants
export const CREATE_EVENT = gql`
  ${EVENT_FIELDS}
  mutation CreateEvent($object: event_insert_input!) {
    insert_event_one(object: $object) {
      ...EventFields
    }
  }
`;

// Update an existing event
export const UPDATE_EVENT = gql`
  ${EVENT_FIELDS}
  mutation UpdateEvent($id: uuid!, $set: event_set_input!) {
    update_event_by_pk(
      pk_columns: { id: $id }
      _set: $set
    ) {
      ...EventFields
    }
  }
`;

// Soft delete an event
export const DELETE_EVENT = gql`
  mutation DeleteEvent($id: uuid!) {
    update_event_by_pk(
      pk_columns: { id: $id }
      _set: { deleted_at: "now()" }
    ) {
      id
      deleted_at
    }
  }
`;

// Upsert event participants (insert or update on conflict)
export const UPSERT_EVENT_PARTICIPANTS = gql`
  mutation UpsertEventParticipants($objects: [event_participant_insert_input!]!) {
    insert_event_participant(
      objects: $objects
      on_conflict: {
        constraint: uq_event_participant_event_person
        update_columns: [status, role, notes]
      }
    ) {
      returning {
        id
        event_id
        person_id
        status
        role
        notes
        created_at
        modified_at
      }
    }
  }
`;

// Remove a participant from an event
export const REMOVE_EVENT_PARTICIPANT = gql`
  mutation RemoveEventParticipant($event_id: uuid!, $person_id: uuid!) {
    delete_event_participant(
      where: {
        event_id: { _eq: $event_id }
        person_id: { _eq: $person_id }
      }
    ) {
      affected_rows
    }
  }
`;

// Update a participant's status
export const UPDATE_PARTICIPANT_STATUS = gql`
  mutation UpdateParticipantStatus($event_id: uuid!, $person_id: uuid!, $status: event_participant_status!) {
    update_event_participant(
      where: {
        event_id: { _eq: $event_id }
        person_id: { _eq: $person_id }
      }
      _set: { status: $status }
    ) {
      returning {
        id
        event_id
        person_id
        status
        modified_at
      }
    }
  }
`;

// Real-time subscription for events (excludes Custody events)
// Uses _or to include recurring events whose base date is before the window
// Note: family_id filtering is handled by Hasura permissions
export const SUBSCRIBE_EVENTS = gql`
  ${EVENT_FIELDS}
  subscription SubscribeEvents($start_date: timestamptz!, $end_date: timestamptz!) {
    event(
      where: {
        deleted_at: { _is_null: true }
        calendar: { _neq: "Custody" }
        _or: [
          { start_time: { _gte: $start_date }, end_time: { _lte: $end_date } }
          { rrule: { _is_null: false }, start_time: { _lte: $end_date } }
        ]
      }
      order_by: { start_time: asc }
    ) {
      ...EventFields
    }
  }
`;

// Subscribe to today's event count (home screen stat, excludes Custody events)
// Note: family_id filtering is handled by Hasura permissions
export const SUBSCRIBE_TODAY_EVENT_COUNT = gql`
  subscription SubscribeTodayEventCount($today_start: timestamptz!, $today_end: timestamptz!) {
    event_aggregate(
      where: {
        deleted_at: { _is_null: true }
        calendar: { _neq: "Custody" }
        start_time: { _gte: $today_start, _lte: $today_end }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

// Query version of today's event count (fallback when subscriptions unavailable)
// Note: family_id filtering is handled by Hasura permissions
export const GET_TODAY_EVENT_COUNT = gql`
  query GetTodayEventCount($today_start: timestamptz!, $today_end: timestamptz!) {
    event_aggregate(
      where: {
        deleted_at: { _is_null: true }
        calendar: { _neq: "Custody" }
        start_time: { _gte: $today_start, _lte: $today_end }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

// ============================================
// CUSTODY EVENT QUERIES AND MUTATIONS
// ============================================

// Subscribe to custody events
// Note: family_id filtering is handled by Hasura permissions
export const SUBSCRIBE_CUSTODY_EVENTS = gql`
  subscription SubscribeCustodyEvents {
    event(
      where: {
        deleted_at: { _is_null: true }
        calendar: { _eq: "Custody" }
      }
      order_by: { start_time: asc }
    ) {
      id
      title
      start_time
      end_time
      rrule
      data
      families {
        family_id
      }
    }
  }
`;

// Query custody events (fallback when subscription isn't working)
// Note: family_id filtering is handled by Hasura permissions
export const GET_CUSTODY_EVENTS = gql`
  query GetCustodyEvents {
    event(
      where: {
        deleted_at: { _is_null: true }
        calendar: { _eq: "Custody" }
      }
      order_by: { start_time: asc }
    ) {
      id
      title
      start_time
      end_time
      rrule
      data
      families {
        family_id
      }
    }
  }
`;

// Bulk insert custody events
export const BULK_INSERT_CUSTODY_EVENTS = gql`
  mutation BulkInsertCustodyEvents($objects: [event_insert_input!]!) {
    insert_event(objects: $objects) {
      affected_rows
    }
  }
`;

// Soft-delete all custody events
// Note: family_id filtering is handled by Hasura permissions
export const DELETE_ALL_CUSTODY_EVENTS = gql`
  mutation DeleteAllCustodyEvents {
    update_event(
      where: {
        deleted_at: { _is_null: true }
        calendar: { _eq: "Custody" }
      }
      _set: { deleted_at: "now()" }
    ) {
      affected_rows
    }
  }
`;

// Upsert a single custody event with plan config in data
export const UPSERT_CUSTODY_EVENT = gql`
  mutation UpsertCustodyEvent($object: event_insert_input!) {
    insert_event_one(object: $object) {
      id
      title
      start_time
      data
    }
  }
`;

// Update an existing custody event
export const UPDATE_CUSTODY_EVENT = gql`
  mutation UpdateCustodyEvent(
    $id: uuid!
    $start_time: timestamptz!
    $end_time: timestamptz!
    $rrule: String
    $data: jsonb!
  ) {
    update_event_by_pk(
      pk_columns: { id: $id }
      _set: {
        start_time: $start_time
        end_time: $end_time
        rrule: $rrule
        data: $data
        modified_at: "now()"
      }
    ) {
      id
      title
      start_time
      end_time
      rrule
      data
    }
  }
`;

// Get events linked to a specific activity (via data JSONB)
export const GET_ACTIVITY_EVENTS = gql`
  query GetActivityEvents($activity_data_id: String!) {
    event(
      where: {
        deleted_at: { _is_null: true }
        data: { _contains: { activityDataId: $activity_data_id } }
      }
      order_by: { start_time: asc }
    ) {
      id
      title
      start_time
      end_time
    }
  }
`;

// Soft-delete all events linked to a specific activity
export const DELETE_ACTIVITY_EVENTS = gql`
  mutation DeleteActivityEvents($activity_data_id: String!) {
    update_event(
      where: {
        deleted_at: { _is_null: true }
        data: { _contains: { activityDataId: $activity_data_id } }
      }
      _set: { deleted_at: "now()" }
    ) {
      affected_rows
    }
  }
`;

// Get events linked to a specific class (via data JSONB)
export const GET_CLASS_EVENTS = gql`
  query GetClassEvents($class_data_id: String!) {
    event(
      where: {
        deleted_at: { _is_null: true }
        data: { _contains: { classDataId: $class_data_id } }
      }
      order_by: { start_time: asc }
    ) {
      id
      title
      start_time
      end_time
    }
  }
`;

// Soft-delete all events linked to a specific class
export const DELETE_CLASS_EVENTS = gql`
  mutation DeleteClassEvents($class_data_id: String!) {
    update_event(
      where: {
        deleted_at: { _is_null: true }
        data: { _contains: { classDataId: $class_data_id } }
      }
      _set: { deleted_at: "now()" }
    ) {
      affected_rows
    }
  }
`;

// ===========================================
// UNIFIED FAMILY DATA QUERIES AND MUTATIONS
// ===========================================
// Data structure: { family_id, data: { type, data: {...} } }

// Generic query to fetch family data by type
// Note: family_id filtering is handled by Hasura permissions
export const GET_FAMILY_DATA = gql`
  query GetFamilyData($type: String!) {
    family_data(
      where: {
        status: { _neq: "D" }
      }
      order_by: { modified_at: desc }
    ) {
      id
      family_id
      data {
        id
        type
        data
      }
      status
      created_at
      modified_at
    }
  }
`;

// Query to fetch all family data (for filtering client-side)
// Note: family_id filtering is handled by Hasura permissions
export const GET_ALL_FAMILY_DATA = gql`
  query GetAllFamilyData {
    family_data(
      where: {
        status: { _neq: "D" }
      }
      order_by: { modified_at: desc }
    ) {
      id
      family_id
      data {
        id
        type
        data
      }
      status
      created_at
      modified_at
    }
  }
`;

// Subscription for real-time family data updates
// Note: family_id filtering is handled by Hasura permissions
export const SUBSCRIBE_FAMILY_DATA = gql`
  subscription SubscribeFamilyData {
    family_data(
      where: {
        status: { _neq: "D" }
      }
      order_by: { modified_at: desc }
    ) {
      id
      family_id
      data {
        id
        type
        data
      }
      status
      created_at
      modified_at
    }
  }
`;

// Query to fetch a single family data item by ID
export const GET_FAMILY_DATA_BY_ID = gql`
  query GetFamilyDataById($id: uuid!) {
    family_data_by_pk(id: $id) {
      id
      family_id
      data {
        id
        type
        data
      }
      status
      created_at
      modified_at
    }
  }
`;

// Insert new family data
export const INSERT_FAMILY_DATA = gql`
  mutation InsertFamilyData($object: family_data_insert_input!) {
    insert_family_data_one(object: $object) {
      id
      family_id
      status
      created_at
      modified_at
    }
  }
`;

// Update family data
// Update the related data record directly
export const UPDATE_FAMILY_DATA = gql`
  mutation UpdateFamilyData($id: uuid!, $type: String!, $data: jsonb!) {
    update_data_by_pk(
      pk_columns: { id: $id }
      _set: { type: $type, data: $data }
    ) {
      id
      type
      data
    }
  }
`;

// Soft delete family data
// Note: family_id filtering is handled by Hasura permissions
export const DELETE_FAMILY_DATA = gql`
  mutation DeleteFamilyData($id: uuid!) {
    update_family_data(
      where: { id: { _eq: $id } }
      _set: { status: "D" }
    ) {
      affected_rows
    }
  }
`;

// ===========================================
// ALBUM QUERIES AND MUTATIONS
// ===========================================

// Fragment for data_attachment fields
export const ATTACHMENT_FIELDS = gql`
  fragment AttachmentFields on data_attachment {
    id
    data_id
    type
    uri
    bucket
    key
    name
    size
    mime_type
    thumbnail
    thumbnail_key
    width
    height
    mini_preview
    created_at
    created_by
  }
`;

// Create a new album (data record with type 'album', linked to family)
export const CREATE_ALBUM = gql`
  mutation CreateAlbum($family_id: uuid!, $data: jsonb!) {
    insert_family_data_one(
      object: {
        family_id: $family_id
        data: {
          data: {
            type: "album"
            data: $data
          }
        }
      }
    ) {
      id
      family_id
      data {
        id
        type
        data
      }
    }
  }
`;

// Get albums
// Note: family_id filtering is handled by Hasura permissions
export const GET_ALBUMS = gql`
  query GetAlbums {
    family_data(
      where: {
        data: { type: { _eq: "album" } }
        status: { _neq: "D" }
      }
      order_by: { modified_at: desc }
    ) {
      id
      family_id
      data {
        id
        type
        data
        data_attachments(
          where: { deleted_at: { _is_null: true } }
          limit: 1
          order_by: { created_at: desc }
        ) {
          id
          uri
          thumbnail_key
          mini_preview
        }
        data_attachments_aggregate(
          where: { deleted_at: { _is_null: true } }
        ) {
          aggregate {
            count
          }
        }
      }
      status
      created_at
      modified_at
    }
  }
`;

// Get media in an album
export const GET_ALBUM_MEDIA = gql`
  ${ATTACHMENT_FIELDS}
  query GetAlbumMedia($album_id: uuid!) {
    data_attachment(
      where: {
        data_id: { _eq: $album_id }
        deleted_at: { _is_null: true }
      }
      order_by: { created_at: desc }
    ) {
      ...AttachmentFields
    }
  }
`;

// Subscribe to album media (real-time updates)
export const SUBSCRIBE_ALBUM_MEDIA = gql`
  ${ATTACHMENT_FIELDS}
  subscription SubscribeAlbumMedia($album_id: uuid!) {
    data_attachment(
      where: {
        data_id: { _eq: $album_id }
        deleted_at: { _is_null: true }
      }
      order_by: { created_at: desc }
    ) {
      ...AttachmentFields
    }
  }
`;

// Add a photo or video to an album (update attachment's data_id)
export const ADD_TO_ALBUM = gql`
  ${ATTACHMENT_FIELDS}
  mutation AddToAlbum($attachment_id: uuid!, $album_id: uuid!) {
    update_data_attachment_by_pk(
      pk_columns: { id: $attachment_id }
      _set: { data_id: $album_id }
    ) {
      ...AttachmentFields
    }
  }
`;

// Remove a photo or video from an album (soft delete)
export const REMOVE_FROM_ALBUM = gql`
  mutation RemoveFromAlbum($attachment_id: uuid!) {
    update_data_attachment_by_pk(
      pk_columns: { id: $attachment_id }
      _set: { deleted_at: "now()" }
    ) {
      id
      deleted_at
    }
  }
`;

// Update album metadata
export const UPDATE_ALBUM = gql`
  mutation UpdateAlbum($id: uuid!, $data: jsonb!) {
    update_data_by_pk(
      pk_columns: { id: $id }
      _set: { type: "album", data: $data }
    ) {
      id
      type
      data
    }
  }
`;

// Save a chat attachment to an album by creating a data_attachment record
export const SAVE_TO_ALBUM = gql`
  ${ATTACHMENT_FIELDS}
  mutation SaveToAlbum(
    $data_id: uuid!
    $type: String!
    $uri: String!
    $bucket: String
    $key: String
    $name: String
    $size: bigint
    $mime_type: String
    $thumbnail: String
    $thumbnail_key: String
    $width: Int
    $height: Int
    $mini_preview: String
    $created_by: uuid!
  ) {
    insert_data_attachment_one(
      object: {
        data_id: $data_id
        type: $type
        uri: $uri
        bucket: $bucket
        key: $key
        name: $name
        size: $size
        mime_type: $mime_type
        thumbnail: $thumbnail
        thumbnail_key: $thumbnail_key
        width: $width
        height: $height
        mini_preview: $mini_preview
        created_by: $created_by
      }
    ) {
      ...AttachmentFields
    }
  }
`;

// Soft delete an album
export const DELETE_ALBUM = gql`
  mutation DeleteAlbum($id: uuid!, $family_id: uuid!) {
    update_family_data(
      where: { id: { _eq: $id }, family_id: { _eq: $family_id } }
      _set: { status: "D" }
    ) {
      affected_rows
    }
  }
`;

// ===========================================
// CHAT QUERIES AND MUTATIONS
// ===========================================

// Get all chats for user's families
// Note: family_id filtering is handled by Hasura permissions
export const GET_FAMILY_CHATS = gql`
  query GetFamilyChats {
    chat(
      where: {
        status: { _neq: "D" }
      }
      order_by: { modified_at: desc }
    ) {
      id
      family_id
      data
      status
      tags
      created_at
      modified_at
      participants {
        user_id
        chat_id
      }
    }
  }
`;

// Create a new chat
export const CREATE_CHAT = gql`
  mutation CreateChat($family_id: uuid!, $data: jsonb, $tags: jsonb, $status: bpchar) {
    insert_chat_one(
      object: {
        family_id: $family_id
        data: $data
        tags: $tags
        status: $status
      }
    ) {
      id
      family_id
      data
      status
      tags
      created_at
      modified_at
    }
  }
`;

// Add a participant to a chat
export const ADD_CHAT_PARTICIPANT = gql`
  mutation AddChatParticipant($chat_id: uuid!, $user_id: uuid!) {
    insert_chat_participant_one(
      object: {
        chat_id: $chat_id
        user_id: $user_id
      }
      on_conflict: {
        constraint: chat_participant_pk
        update_columns: []
      }
    ) {
      chat_id
      user_id
    }
  }
`;

// Add multiple participants to a chat
export const ADD_CHAT_PARTICIPANTS = gql`
  mutation AddChatParticipants($objects: [chat_participant_insert_input!]!) {
    insert_chat_participant(
      objects: $objects
      on_conflict: {
        constraint: chat_participant_pk
        update_columns: []
      }
    ) {
      affected_rows
    }
  }
`;

// Remove a participant from a chat
export const REMOVE_CHAT_PARTICIPANT = gql`
  mutation RemoveChatParticipant($chat_id: uuid!, $user_id: uuid!) {
    delete_chat_participant_by_pk(chat_id: $chat_id, user_id: $user_id) {
      chat_id
      user_id
    }
  }
`;

// Get chats for a specific user
export const GET_USER_CHATS = gql`
  query GetUserChats($user_id: uuid!) {
    chat_participant(
      where: { user_id: { _eq: $user_id } }
    ) {
      chat {
        id
        family_id
        data
        status
        tags
        created_at
        modified_at
        participants {
          user_id
          chat_id
        }
      }
    }
  }
`;

// Subscribe to user's chats (real-time updates)
export const SUBSCRIBE_USER_CHATS = gql`
  subscription SubscribeUserChats($user_id: uuid!) {
    chat_participant(
      where: { user_id: { _eq: $user_id } }
    ) {
      chat {
        id
        family_id
        data
        status
        tags
        created_at
        modified_at
        participants {
          user_id
          chat_id
        }
      }
    }
  }
`;

// Subscribe to family chats (real-time updates)
// Note: family_id filtering is handled by Hasura permissions
export const SUBSCRIBE_FAMILY_CHATS = gql`
  subscription SubscribeFamilyChats {
    chat(
      where: {
        status: { _neq: "D" }
      }
      order_by: { modified_at: desc }
    ) {
      id
      family_id
      data
      status
      tags
      created_at
      modified_at
      participants {
        user_id
        chat_id
      }
    }
  }
`;

// ===========================================
// MESSAGE QUERIES AND MUTATIONS
// ===========================================

// Get messages for a chat
export const GET_CHAT_MESSAGES = gql`
  query GetChatMessages($chat_id: uuid!, $limit: Int = 50, $offset: Int = 0) {
    message(
      where: {
        chat_id: { _eq: $chat_id }
        status: { _neq: "D" }
      }
      order_by: { created_at: desc }
      limit: $limit
      offset: $offset
    ) {
      id
      chat_id
      sender_id
      data
      status
      created_at
      modified_at
    }
  }
`;

// Subscribe to messages in a chat (real-time updates)
export const SUBSCRIBE_CHAT_MESSAGES = gql`
  subscription SubscribeChatMessages($chat_id: uuid!, $limit: Int = 50) {
    message(
      where: {
        chat_id: { _eq: $chat_id }
        status: { _neq: "D" }
      }
      order_by: { created_at: desc }
      limit: $limit
    ) {
      id
      chat_id
      sender_id
      text
      data
      status
      created_at
      modified_at
    }
  }
`;

// Send a new message
export const SEND_MESSAGE = gql`
  mutation SendMessage(
    $chat_id: uuid!
    $sender_id: uuid!
    $text: String!
    $data: jsonb
  ) {
    insert_message_one(
      object: {
        chat_id: $chat_id
        sender_id: $sender_id
        text: $text
        data: $data
        status: "A"
      }
    ) {
      id
      chat_id
      sender_id
      text
      data
      status
      created_at
      modified_at
    }
  }
`;

// Update a message (for editing)
export const UPDATE_MESSAGE = gql`
  mutation UpdateMessage($id: uuid!, $data: jsonb!) {
    update_message_by_pk(
      pk_columns: { id: $id }
      _set: { data: $data }
    ) {
      id
      chat_id
      sender_id
      data
      status
      created_at
      modified_at
    }
  }
`;

// Soft delete a message
export const DELETE_MESSAGE = gql`
  mutation DeleteMessage($id: uuid!) {
    update_message_by_pk(
      pk_columns: { id: $id }
      _set: { status: "D" }
    ) {
      id
    }
  }
`;

// Update chat's last message and modified_at
export const UPDATE_CHAT_LAST_MESSAGE = gql`
  mutation UpdateChatLastMessage($chat_id: uuid!, $data: jsonb!) {
    update_chat_by_pk(
      pk_columns: { id: $chat_id }
      _set: { data: $data }
    ) {
      id
      data
      modified_at
    }
  }
`;

// Agent conversation queries
export const GET_AGENT_CONVERSATIONS = gql`
  query GetAgentConversations($userId: uuid!) {
    agent_conversation(
      where: { user_id: { _eq: $userId } }
      order_by: { modified_at: desc }
      limit: 1
    ) {
      id
      created_at
      modified_at
      agent_messages(order_by: { created_at: asc }) {
        id
        role
        content
        tool_call_id
        tool_name
        tool_arguments
        created_at
      }
    }
  }
`;

// ============================================
// EMAIL QUERIES
// ============================================

export const GET_FAMILY_EMAILS = gql`
  query GetFamilyEmails {
    email(order_by: { received_at: desc }) {
      id
      family_id
      from_address
      to_address
      subject
      body_text
      body_html
      received_at
      category
      data
      created_at
      direction
      message_id
      in_reply_to
      email_people {
        person_id
      }
    }
  }
`;

export const GET_EMAIL_BY_ID = gql`
  query GetEmailById($id: uuid!) {
    email_by_pk(id: $id) {
      id
      family_id
      from_address
      to_address
      subject
      body_text
      body_html
      received_at
      category
      data
      created_at
      direction
      message_id
      in_reply_to
      email_people {
        person_id
      }
    }
  }
`;
